import React, { lazy, Suspense } from "react";
import ReactDOM, { type Root } from "react-dom/client";
import "./index.css";
import "./image-dimensions.css"; // Import new CSS rules for image dimensions
import "./mobile-optimized.css"; // Mobile-optimized CSS
import { initializeSupabase } from './database/supabase-client';
import { clearAllAvatarCaches } from './components/ui/CachedAvatarImage';
// SECURITY: Import production cleanup utilities
import '@/utils/productionCleanup';

type MobileAwareWindow = Window & {
  __isMobile?: boolean;
};

type IdleCapableWindow = Window & {
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
};

type BootDebugEntry = {
  ts: number;
  sinceStartMs: number;
  phase: string;
  detail?: unknown;
};

type BootDebugState = {
  startedAt: number;
  entries: BootDebugEntry[];
};

type WindowWithBootDebug = Window & {
  __nsBootDebug?: BootDebugState;
  dumpBootDebug?: () => BootDebugEntry[];
};

// Mobile device detection
const mobileAwareWindow = typeof window !== 'undefined' ? (window as MobileAwareWindow) : undefined;
const isMobile = typeof window !== 'undefined' && 
  (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
  mobileAwareWindow?.__isMobile === true);
const APP_RESUMED_EVENT = 'novelsprint:app-resumed';
const isIOSDevice = typeof window !== 'undefined' &&
  /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isStandaloneDisplayMode = typeof window !== 'undefined' && (
  window.matchMedia?.('(display-mode: standalone)').matches === true ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true
);
const isIOSStandalone = isIOSDevice && isStandaloneDisplayMode;

const dispatchAppResumedEvent = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(APP_RESUMED_EVENT));
};

const BOOT_DEBUG_STORAGE_KEY = 'ns_boot_debug_trace';
const BOOT_DEBUG_MAX_ENTRIES = 80;

const pushBootDebug = (phase: string, detail?: unknown) => {
  if (typeof window === 'undefined') return;
  try {
    const debugWindow = window as WindowWithBootDebug;
    if (!debugWindow.__nsBootDebug) {
      debugWindow.__nsBootDebug = {
        startedAt: Date.now(),
        entries: [],
      };
    }

    const state = debugWindow.__nsBootDebug;
    const entry: BootDebugEntry = {
      ts: Date.now(),
      sinceStartMs: Date.now() - state.startedAt,
      phase,
      detail,
    };
    state.entries.push(entry);
    if (state.entries.length > BOOT_DEBUG_MAX_ENTRIES) {
      state.entries = state.entries.slice(-BOOT_DEBUG_MAX_ENTRIES);
    }
    sessionStorage.setItem(BOOT_DEBUG_STORAGE_KEY, JSON.stringify(state.entries));

    const params = new URLSearchParams(window.location.search);
    if (params.get('bootDebug') === '1') {
      console.log('[BootDebug]', entry);
    }
  } catch {
    // ignore boot debug failures
  }
};

const hasPersistedActiveSprint = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem('write-store-settings');
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const state = parsed?.state;
    if (!state || typeof state !== 'object') return false;

    const isRunning = state.isRunning === true;
    const isPaused = state.isPaused === true;
    const hasSprintId = typeof state.sprintId === 'string' && state.sprintId.length > 0;
    const hasStartTime = typeof state.startTime === 'number' && Number.isFinite(state.startTime) && state.startTime > 0;

    return (isRunning || isPaused) && hasSprintId && hasStartTime;
  } catch {
    return false;
  }
};

// Add a mobile-specific CSS class to the body
if (typeof document !== 'undefined' && isMobile) {
  document.body.classList.add('mobile-device');
}

// **AUTOMATIC CACHE CLEARING ON START FOR HIGH-RES AVATARS**
if (typeof window !== 'undefined') {
  // Run only once per session
  if (!sessionStorage.getItem('avatar_cache_cleared_v8')) {
    clearAllAvatarCaches();
    sessionStorage.setItem('avatar_cache_cleared_v8', 'true');
    // Debug log removed for production
  }
}

if (typeof window !== 'undefined') {
  let hiddenAt = 0;
  const IOS_RESUME_RELOAD_GUARD_KEY = 'ns_ios_resume_reload';
  const IOS_RESUME_RELOAD_WINDOW_MS = 45_000;
  const IOS_RESUME_RELOAD_GUARD_MS = 20_000;

  const tryIOSStandaloneResumeReload = (reason: string) => {
    if (!isIOSStandalone) {
      dispatchAppResumedEvent();
      return;
    }

    if (hasPersistedActiveSprint()) {
      console.warn(`[App] Skip iOS resume reload (${reason}) because an active sprint is running.`);
      dispatchAppResumedEvent();
      return;
    }

    const now = Date.now();
    const lastReloadRaw = sessionStorage.getItem(IOS_RESUME_RELOAD_GUARD_KEY);
    const lastReload = lastReloadRaw ? Number(lastReloadRaw) : 0;

    if (Number.isFinite(lastReload) && now - lastReload < IOS_RESUME_RELOAD_GUARD_MS) {
      dispatchAppResumedEvent();
      return;
    }

    sessionStorage.setItem(IOS_RESUME_RELOAD_GUARD_KEY, String(now));
    console.warn(`[App] Reloading iOS standalone app after resume (${reason}) to prevent stale state.`);
    window.location.reload();
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }

    const hiddenDuration = hiddenAt > 0 ? Date.now() - hiddenAt : 0;
    if (hiddenDuration >= IOS_RESUME_RELOAD_WINDOW_MS) {
      tryIOSStandaloneResumeReload('visibility');
      return;
    }
    dispatchAppResumedEvent();
  });

  window.addEventListener('pageshow', (event) => {
    if ((event as PageTransitionEvent).persisted) {
      tryIOSStandaloneResumeReload('bfcache');
      return;
    }
    dispatchAppResumedEvent();
  });

  window.addEventListener('online', () => {
    dispatchAppResumedEvent();
  });
}

// **SERVICE WORKER REGISTRATION - ALSO IN DEV MODE**
if ('serviceWorker' in navigator) {
  if (import.meta.env.PROD) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Avoid force-reloading on controller changes.
      // Reloading here can break OAuth callback flows because the one-time `code`
      // in `/auth/callback?code=...` may already be consumed after a refresh.
      const isAuthCallback = window.location.pathname.startsWith('/auth/callback');
      const hasOAuthCode = new URLSearchParams(window.location.search).has('code');
      if (isAuthCallback || hasOAuthCode) {
        return;
      }
      dispatchAppResumedEvent();
    });

    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
        .then((registration) => {
          const activateWaitingWorker = () => {
            if (registration.waiting) {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
          };

          const triggerUpdateCheck = () => {
            registration.update().catch(() => undefined);
          };

          activateWaitingWorker();
          registration.addEventListener('updatefound', () => {
            const installingWorker = registration.installing;
            if (!installingWorker) return;
            installingWorker.addEventListener('statechange', () => {
              if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
                activateWaitingWorker();
              }
            });
          });

          setInterval(triggerUpdateCheck, 5 * 60 * 1000);
          window.addEventListener('online', triggerUpdateCheck);
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
              triggerUpdateCheck();
            }
          });
        })
        .catch((error) => {
          console.error('[SW] ❌ Service Worker registration failed:', error);
        });
    });
  } else {
    navigator.serviceWorker.getRegistrations()
      .then((registrations) => Promise.all(registrations.map((r) => r.unregister())))
      .catch(() => undefined);
  }
}

// Utility functions for service worker communication
export const clearAvatarServiceWorkerCache = () => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CLEAR_AVATAR_CACHE'
    });
  }
};

export const invalidateAvatarUrlInServiceWorker = (url: string) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller && url) {
    navigator.serviceWorker.controller.postMessage({
      type: 'INVALIDATE_AVATAR_URL',
      url: url
    });
  }
};

// **LAZY LOADING WITH RETRY** for App component
const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    promise
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });

const App = lazy(() =>
  withTimeout(import('./App'), 15000, 'App chunk loading').catch(() => {
    // Retry once after short delay
    return new Promise<typeof import('./App')>((resolve, reject) =>
      setTimeout(() => {
        withTimeout(import('./App'), 15000, 'App chunk retry loading')
          .then(resolve)
          .catch((error) => {
            const reloadGuardKey = 'ns_bootstrap_chunk_reload';
            const now = Date.now();
            const lastReload = sessionStorage.getItem(reloadGuardKey);
            const recentlyReloaded = lastReload && now - Number(lastReload) < 30000;
            const sprintActive = hasPersistedActiveSprint();

            if (!recentlyReloaded && !sprintActive) {
              sessionStorage.setItem(reloadGuardKey, String(now));

              if ('serviceWorker' in navigator) {
                navigator.serviceWorker
                  .getRegistrations()
                  .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
                  .catch(() => undefined)
                  .finally(() => window.location.reload());
              } else {
                window.location.reload();
              }
              return;
            }

            if (sprintActive) {
              console.warn('[App] Skip bootstrap hard reload because an active sprint is running.');
              dispatchAppResumedEvent();
            }

            reject(error);
          });
      }, 1000)
    );
  })
);

// Import the AppErrorBoundary
import { AppErrorBoundary } from './components/common/ErrorBoundary'; 

// **STRIPE LAZY LOADING - ONLY WHEN NEEDED**
const loadStripeAsync = async (key: string) => {
  // Lazy-load Stripe only when subscription features are needed
  return new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      const idleWindow = window as IdleCapableWindow;
      idleWindow.requestIdleCallback?.(() => {
        import("@stripe/stripe-js").then(({ loadStripe }) => {
          resolve(loadStripe(key));
        });
      }, { timeout: 8000 }); // Longer delay
    } else {
      setTimeout(() => {
        import("@stripe/stripe-js").then(({ loadStripe }) => {
          resolve(loadStripe(key));
        });
      }, 5000); // Even longer delay for dev
    }
  });
};

// Simple mobile detection without problematic optimizations
if (typeof window !== 'undefined') {
  // Add a mobile class to the body when on a mobile device
  if (isMobile && document.body) {
    document.body.classList.add('mobile-device');
  }
}

const main = async () => {
  const BOOTSTRAP_TIMEOUT_MS = 25000;
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  const rootElement = document.getElementById("root");
  pushBootDebug('main:start');
  if (!rootElement) throw new Error("Failed to find the root element");
  const windowWithRoot = window as Window & { __NOVELSPRINT_REACT_ROOT__?: Root };
  const root = windowWithRoot.__NOVELSPRINT_REACT_ROOT__ ?? ReactDOM.createRoot(rootElement);
  windowWithRoot.__NOVELSPRINT_REACT_ROOT__ = root;
  let bootstrapWatchdog: ReturnType<typeof setTimeout> | null = null;

  // Render an initial fallback/loading indicator
  pushBootDebug('main:initial-render');
  root.render(
    <React.StrictMode>
      <div className="flex h-screen items-center justify-center">
        <div className="animate-pulse text-primary text-2xl">Loading NovelSprint...</div>
      </div>
    </React.StrictMode>
  );

  bootstrapWatchdog = setTimeout(() => {
    pushBootDebug('main:watchdog-timeout', { timeoutMs: BOOTSTRAP_TIMEOUT_MS });
    root.render(
      <React.StrictMode>
        <div className="flex h-screen items-center justify-center bg-red-100 text-red-700 p-4">
          <p>
            Initialization timed out. Please reload the page. If this persists, clear site data (cache + service worker).
          </p>
        </div>
      </React.StrictMode>
    );
  }, BOOTSTRAP_TIMEOUT_MS);

  if (!supabaseUrl || !supabaseAnonKey || !stripePublicKey) {
    pushBootDebug('main:env-missing', {
      hasSupabaseUrl: Boolean(supabaseUrl),
      hasSupabaseAnonKey: Boolean(supabaseAnonKey),
      hasStripePublicKey: Boolean(stripePublicKey),
    });
    root.render(
      <React.StrictMode>
        <div className="flex h-screen items-center justify-center bg-red-100 text-red-700 p-4">
          <p>
            Error: Environment variables for Supabase or Stripe are not set.
            Please check your <code>.env</code> file or server configuration.
          </p>
        </div>
      </React.StrictMode>,
    );
    return; // Stop execution if required variables are missing
  }

  try {
    pushBootDebug('main:supabase-init:start');
    // 1. Initialize Supabase centrally for critical functionality
    const supabaseInitializedClient = await withTimeout(
      initializeSupabase({
        url: supabaseUrl, 
        anonKey: supabaseAnonKey 
      }),
      12000,
      'Supabase bootstrap'
    );

    // Optional: Verify the instance was returned correctly
    if (!supabaseInitializedClient) {
        throw new Error("initializeSupabase did not return a client.");
    }
    pushBootDebug('main:supabase-init:done');

    // 2. Load Stripe in parallel - initialized only when needed
    // Note: We start loading here but do not explicitly await it
    const stripePromise = loadStripeAsync(stripePublicKey);
    void stripePromise;
    pushBootDebug('main:stripe-init:scheduled');

    // 3. Render the app with Suspense for better UX during loading
    root.render(
      <React.StrictMode>
        <AppErrorBoundary> { /* App-level error boundary */ }
          <Suspense fallback={<div className="flex h-screen items-center justify-center"><div className="animate-pulse text-primary text-2xl">Loading NovelSprint...</div></div>}>
            <App />
          </Suspense>
        </AppErrorBoundary>
      </React.StrictMode>
    );
    pushBootDebug('main:app-rendered');

  } catch (error) {
    pushBootDebug('main:error', {
      message: error instanceof Error ? error.message : String(error),
    });
    console.error("[main.tsx] Error during initialization:", error);
    // Show error in the UI
    root.render(
      <React.StrictMode>
        <div className="flex h-screen items-center justify-center bg-red-100 text-red-700 p-4">
          <p>
            An initialization error occurred. Please try again later.
            {error instanceof Error ? ` Details: ${error.message}` : ""}
          </p>
        </div>
      </React.StrictMode>,
    );
  } finally {
    if (bootstrapWatchdog) {
      clearTimeout(bootstrapWatchdog);
    }
    pushBootDebug('main:finalized');
  }
};

main().catch((error) => {
  pushBootDebug('main:unhandled-error', {
    message: error instanceof Error ? error.message : String(error),
  });
  // Catch errors that occur outside the try-catch blocks in main()
  console.error("Unhandled error while starting the application:", error);
  const rootElement = document.getElementById("root");
  if (rootElement) {
    const windowWithRoot = window as Window & { __NOVELSPRINT_REACT_ROOT__?: Root };
    const root = windowWithRoot.__NOVELSPRINT_REACT_ROOT__ ?? ReactDOM.createRoot(rootElement);
    windowWithRoot.__NOVELSPRINT_REACT_ROOT__ = root;
    root.render(
      <React.StrictMode>
        <div className="flex h-screen items-center justify-center bg-red-100 text-red-700 p-4">
          <p>
            A critical error occurred while starting the application.
          </p>
        </div>
      </React.StrictMode>,
    );
  }
});
